import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const LEVELS = ['A1', 'A2', 'B1', 'B2'];

// ── Level tab component (shared pattern) ──────────────────────
function LevelTabs({ active, onChange }) {
  return (
    <div className="flex gap-0 rounded-xl overflow-hidden border border-gray-200 w-fit shadow-sm">
      {LEVELS.map((lv, i) => (
        <button
          key={lv}
          onClick={() => onChange(lv)}
          className="relative px-5 py-2.5 text-sm font-bold transition-all duration-200 focus:outline-none"
          style={{
            background:   active === lv ? '#DD0000' : '#FFFFFF',
            color:        active === lv ? '#FFFFFF' : '#1A1A1A',
            borderRight:  i < LEVELS.length - 1 ? '1px solid #E0E0E0' : 'none',
          }}
          onMouseEnter={e => { if (active !== lv) e.currentTarget.style.background = '#FFF0F0'; }}
          onMouseLeave={e => { if (active !== lv) e.currentTarget.style.background = '#FFFFFF'; }}
        >
          {active === lv && (
            <span
              className="absolute bottom-0 left-0 right-0 h-0.5"
              style={{ background: '#FFCE00' }}
            />
          )}
          {lv}
        </button>
      ))}
    </div>
  );
}

export default function StudentMoviesPage() {
  const { user }     = useAuth();
  const { addToast } = useToast();

  const currentLevel   = user?.student?.currentLevel || user?.currentLevel || 'A1';
  const unlockedLevels = user?.student?.unlockedLevels || user?.unlockedLevels || ['A1'];

  const [movies, setMovies]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeLevel, setActiveLevel] = useState(currentLevel);
  const [playing, setPlaying]     = useState(null); // { youtubeId, title, description, genre }

  useEffect(() => {
    API.get('/movies')
      .then(({ data }) => setMovies(data.movies || []))
      .catch(() => addToast('Failed to load movies', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // Client-side level filter
  const filtered   = movies.filter(m => m.level === activeLevel);
  const isLocked   = lv => !unlockedLevels.includes(lv);

  const handleLevelChange = (lv) => {
    setActiveLevel(lv);
    setPlaying(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <SectionHeader
          title="German Movies"
          subtitle="Watch German films to improve your listening and vocabulary"
        />
        <LevelTabs active={activeLevel} onChange={handleLevelChange} />
      </div>

      {/* Locked level notice */}
      {isLocked(activeLevel) && (
        <div
          className="mb-5 p-3 rounded-xl border flex items-center gap-3"
          style={{ background: '#FFF8E1', borderColor: '#FFCE00' }}
        >
          <span className="text-xl">🔒</span>
          <p className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>
            Level {activeLevel} is locked. Complete your current level to unlock.
          </p>
        </div>
      )}

      {/* Embedded player */}
      {playing && (
        <div
          className="mb-8 rounded-2xl overflow-hidden shadow-2xl border border-gray-200"
          style={{ background: '#000' }}
        >
          <div
            className="relative w-full"
            style={{ paddingBottom: '56.25%', height: 0 }}
          >
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={`https://www.youtube.com/embed/${playing.youtubeId}?autoplay=1&rel=0`}
              title={playing.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <div
            className="px-6 py-4 flex items-start justify-between gap-4"
            style={{ background: '#1A1A1A' }}
          >
            <div>
              <h3 className="font-bold text-base text-white">{playing.title}</h3>
              {playing.description && (
                <p className="text-sm mt-1" style={{ color: '#9CA3AF' }}>
                  {playing.description}
                </p>
              )}
              {playing.genre && (
                <span
                  className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-semibold border"
                  style={{
                    color:        '#FFCE00',
                    borderColor:  'rgba(255,206,0,0.3)',
                    background:   'rgba(255,206,0,0.1)',
                  }}
                >
                  {playing.genre}
                </span>
              )}
            </div>
            <button
              onClick={() => setPlaying(null)}
              className="flex-shrink-0 text-xl leading-none mt-0.5 transition-colors"
              style={{ color: '#9CA3AF' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#FFFFFF'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#9CA3AF'; }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" className="text-brand-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-5xl mb-4">🎞️</p>
          <p className="text-base font-bold mb-1" style={{ color: '#1A1A1A' }}>
            No movies for Level {activeLevel}
          </p>
          <p className="text-sm" style={{ color: '#888' }}>
            Your teacher hasn't added any German movies for this level yet.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filtered.map(movie => {
            const isPlaying = playing?.youtubeId === movie.youtube_id;
            return (
              <button
                key={movie.id}
                onClick={() =>
                  setPlaying({
                    youtubeId:   movie.youtube_id,
                    title:       movie.title,
                    description: movie.description,
                    genre:       movie.genre,
                  })
                }
                className="text-left rounded-xl overflow-hidden transition-all duration-200 group"
                style={{
                  border:     isPlaying ? '2px solid #DD0000' : '2px solid transparent',
                  transform:  isPlaying ? 'scale(1.03)' : 'scale(1)',
                  boxShadow:  isPlaying
                    ? '0 8px 24px rgba(221,0,0,0.2)'
                    : '0 1px 4px rgba(0,0,0,0.08)',
                }}
                onMouseEnter={e => {
                  if (!isPlaying) {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow   = '0 8px 24px rgba(0,0,0,0.12)';
                  }
                }}
                onMouseLeave={e => {
                  if (!isPlaying) {
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.boxShadow   = '0 1px 4px rgba(0,0,0,0.08)';
                  }
                }}
              >
                {/* Poster — 2:3 aspect ratio */}
                <div
                  className="relative overflow-hidden"
                  style={{ paddingBottom: '150%', background: '#111' }}
                >
                  <img
                    src={
                      movie.thumbnail_url ||
                      `https://img.youtube.com/vi/${movie.youtube_id}/maxresdefault.jpg`
                    }
                    alt={movie.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={e => {
                      e.target.src = `https://img.youtube.com/vi/${movie.youtube_id}/hqdefault.jpg`;
                    }}
                  />

                  {/* Gradient overlay */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 50%)',
                    }}
                  />

                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center shadow-2xl"
                      style={{ background: 'rgba(221,0,0,0.9)' }}
                    >
                      <span className="text-white text-xl ml-1">▶</span>
                    </div>
                  </div>

                  {/* Genre badge */}
                  {movie.genre && (
                    <span
                      className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-xs font-medium text-white"
                      style={{ background: 'rgba(0,0,0,0.65)' }}
                    >
                      {movie.genre}
                    </span>
                  )}

                  {/* Currently playing badge */}
                  {isPlaying && (
                    <div
                      className="absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-bold text-white"
                      style={{ background: '#DD0000' }}
                    >
                      ▶ Playing
                    </div>
                  )}

                  {/* Title at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-2">
                    <p className="text-white text-xs font-semibold leading-tight line-clamp-2">
                      {movie.title}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
